import React, {
 useState
} from 'react';
import {
    Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from 'shared/components/ui/table';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from 'shared/components/ui/dialog';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Switch
} from 'shared/components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'shared/components/ui/select';
import {
 Plus, Pencil, Trash2, GitFork
} from 'lucide-react';
import {
 SchemaRelationship, SchemaField
} from '../../types/schema.types';

interface SchemaRelationshipsTabProps {
    relationships: SchemaRelationship[];
    setRelationships: React.Dispatch<React.SetStateAction<SchemaRelationship[]>>;
    currentSchemaName: string;
    fields: SchemaField[];
    allSchemaNames: string[];
}

const emptyRelationship: Omit<SchemaRelationship, 'id'> = {
    name: '',
    targetSchema: '',
    type: 'N:1',
    sourceField: '',
    targetField: '',
    cascadeDelete: false,
    cascadeUpdate: false,
};

const relationshipTypeLabels: Record<string, string> = {
    '1:1': 'One-to-One',
    '1:N': 'One-to-Many',
    'N:1': 'Many-to-One',
};

let relIdCounter = 0;

const SchemaRelationshipsTab: React.FC<SchemaRelationshipsTabProps> = ({
    relationships, setRelationships, currentSchemaName, fields, allSchemaNames,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [editingRel, setEditingRel] = useState<SchemaRelationship | null>(null);
    const [formData, setFormData] = useState(emptyRelationship);

    const openAddModal = () => {
        setEditingRel(null);
        setFormData(emptyRelationship);
        setShowModal(true);
    };

    const openEditModal = (rel: SchemaRelationship) => {
        setEditingRel(rel);
        setFormData({
            name: rel.name,
            targetSchema: rel.targetSchema,
            type: rel.type,
            sourceField: rel.sourceField,
            targetField: rel.targetField,
            cascadeDelete: rel.cascadeDelete,
            cascadeUpdate: rel.cascadeUpdate,
        });
        setShowModal(true);
    };

    const handleSave = () => {
        if (!formData.name || !formData.targetSchema || !formData.sourceField) return;

        if (editingRel) {
            setRelationships((prev) =>
                prev.map((r) =>
                    r.id === editingRel.id ? {
 ...r,
...formData
} : r
                )
            );
        } else {
            relIdCounter += 1;
            const newRel: SchemaRelationship = {
                id: `rel_${Date.now()}_${relIdCounter}`,
                ...formData,
                isNew: true,
            };
            setRelationships((prev) => [...prev, newRel]);
        }
        setShowModal(false);
    };

    const handleDelete = (relId: string) => {
        setRelationships((prev) => prev.filter((r) => r.id !== relId));
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                    Define how this schema relates to other schemas.
                </p>
                <Button size="sm" onClick={openAddModal}>
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Add Relationship
                </Button>
            </div>

            {relationships.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3 border rounded-md">
                    <GitFork className="h-10 w-10 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">No relationships defined.</p>
                    <Button size="sm" variant="outline" onClick={openAddModal}>
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Add Your First Relationship
                    </Button>
                </div>
            ) : (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Target Schema</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Join</TableHead>
                                <TableHead className="w-[100px] text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {relationships.map((rel) => (
                                <TableRow key={rel.id}>
                                    <TableCell className="font-medium">{rel.name}</TableCell>
                                    <TableCell>{rel.targetSchema}</TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="">
                                            {relationshipTypeLabels[rel.type] || rel.type}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {rel.sourceField} → {rel.targetField}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7"
                                                onClick={() => openEditModal(rel)}
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 text-red-500 hover:text-red-700"
                                                onClick={() => handleDelete(rel.id)}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Add/Edit Modal */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editingRel ? 'Edit Relationship' : 'Add Relationship'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label className="text-sm">Relationship Name</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData((p) => ({
 ...p,
name: e.target.value
}))}
                                placeholder="e.g., author, products"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <Label className="text-sm">Target Schema</Label>
                            <Select
                                value={formData.targetSchema}
                                onValueChange={(v) => setFormData((p) => ({
 ...p,
targetSchema: v
}))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select target schema" />
                                </SelectTrigger>
                                <SelectContent>
                                    {allSchemaNames
                                        .filter((n) => n !== currentSchemaName)
                                        .map((name) => (
                                            <SelectItem key={name} value={name}>
                                                {name}
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-1.5">
                            <Label className="text-sm">Relationship Type</Label>
                            <div className="grid grid-cols-3 gap-2">
                                {(['1:1', '1:N', 'N:1'] as const).map((type) => (
                                    <button
                                        key={type}
                                        type="button"
                                        className={`p-2 border rounded-md text-center text-sm transition-colors ${
                                            formData.type === type
                                                ? 'border-primary bg-primary/5 font-medium'
                                                : 'border-muted hover:bg-muted/50'
                                        }`}
                                        onClick={() => setFormData((p) => ({
 ...p,
type
}))}
                                    >
                                        {relationshipTypeLabels[type]}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label className="text-sm">Source Field ({currentSchemaName})</Label>
                                <Select
                                    value={formData.sourceField}
                                    onValueChange={(v) => setFormData((p) => ({
 ...p,
sourceField: v
}))}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select field" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {fields.map((f) => (
                                            <SelectItem key={f.id} value={f.columnName}>
                                                {f.columnName}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-1.5">
                                <Label className="text-sm">Target Field</Label>
                                <Input
                                    value={formData.targetField}
                                    onChange={(e) => setFormData((p) => ({
 ...p,
targetField: e.target.value
}))}
                                    placeholder="e.g., id"
                                />
                            </div>
                        </div>

                        <div className="space-y-2.5 pt-2">
                            <Label className="text-sm font-medium">Cascade Settings</Label>
                            <div className="flex items-center justify-between">
                                <Label className="text-xs text-muted-foreground">Cascade Delete</Label>
                                <Switch
                                    checked={formData.cascadeDelete}
                                    onCheckedChange={(v) => setFormData((p) => ({
 ...p,
cascadeDelete: v
}))}
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <Label className="text-xs text-muted-foreground">Cascade Update</Label>
                                <Switch
                                    checked={formData.cascadeUpdate}
                                    onCheckedChange={(v) => setFormData((p) => ({
 ...p,
cascadeUpdate: v
}))}
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={!formData.name || !formData.targetSchema || !formData.sourceField}
                        >
                            {editingRel ? 'Update' : 'Add'} Relationship
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SchemaRelationshipsTab;
